import { ActivityIndicator, View } from "@-/com.npmjs.react-native";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useEffect, useMemo } from "react";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { SpaceBackdrop } from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import {
  learnSummaryQueueHead,
  useLearnSummaryPageNavigator,
  useLearnSummaryPageQueryOptions,
  type LearnSummaryQueueHead,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import type { LearnDeckEntry } from "#learn-deck-entry";

/**
 * The head of the study queue as the deck host last observed it. `entry: null` means there is
 * nothing to study.
 */
export type LearnDeckQueueHead =
  | { status: "pending" }
  | { retry: () => void; status: "error" }
  | { entry: LearnDeckEntry | null; status: "success" };

function entryForQueueHead(head: LearnSummaryQueueHead): LearnDeckEntry {
  switch (head.kind) {
    case "review": {
      return {
        contextSentence: null,
        drillType: head.drillType,
        face: "front",
        grapheme: head.grapheme,
        kind: "review",
        mode: head.studyMode,
        view: "study",
      };
    }
    case "slide": {
      return { face: "front", kind: "slide", slideSlug: head.slideSlug, view: "study" };
    }
    case "quiz": {
      return {
        face: "front",
        kind: "quiz",
        quizSlug: head.quizSlug,
        source: head.source,
        view: "study",
      };
    }
    case "newMorpheme": {
      return {
        face: "front",
        grapheme: head.morpheme,
        kind: "lesson",
        ...(head.part === undefined ? {} : { part: head.part }),
        ...(head.pinyin === undefined ? {} : { pinyin: head.pinyin }),
        view: "study",
      };
    }
  }
}

/**
 * Observes the content-validated summary resolution and reports its queue head. The host mounts
 * it only while something needs the head (the summary's preview, an unresolved Start): the
 * resolution re-runs on every study-table change, so studying must not keep it observed.
 */
export function LearnDeckQueueHeadObserver({
  onChange,
}: {
  onChange: (head: LearnDeckQueueHead) => void;
}) {
  const options = useLearnSummaryPageQueryOptions({ validateContent: true });
  const { data, refetch, status } = useQuery({ ...options, throwOnError: false });
  const [studyEnvironment] = useDeviceSetting("studyEnvironment");
  const entry = useMemo(() => {
    if (data === undefined) {
      return undefined;
    }
    const head = learnSummaryQueueHead(data, studyEnvironment);
    return head === null ? null : entryForQueueHead(head);
  }, [data, studyEnvironment]);
  useEffect(() => {
    onChange(
      entry !== undefined
        ? { entry, status: "success" }
        : status === "error"
          ? { retry: () => void refetch(), status: "error" }
          : { status: "pending" },
    );
  }, [entry, onChange, refetch, status]);
  return null;
}

/**
 * The deck route while Start's card is still being resolved, failed to resolve, or turned out not
 * to exist. Every state has an exit: the resolution is a bounded query, its error retries it from
 * scratch, and both dead ends lead back to Learn.
 */
export function LearnDeckQueueHeadStatus({ head }: { head: LearnDeckQueueHead }) {
  const summaryPageNavigator = useLearnSummaryPageNavigator();
  const goToLearn = (
    <Button
      className="mt-2"
      disabled={summaryPageNavigator.replace.isPending}
      onPress={() => {
        summaryPageNavigator.replace.mutate();
      }}
      testID="learn-deck-queue-head-go-to-learn"
      variant="outline"
    >
      <Text>Go to Learn</Text>
    </Button>
  );
  return (
    <View
      className="absolute inset-0 z-10 items-center justify-center gap-4 bg-muted px-6"
      testID={`learn-deck-queue-head-${head.status === "success" ? "empty" : head.status}`}
    >
      <SpaceBackdrop />
      <PrimaryControlsStripTitle>Learn</PrimaryControlsStripTitle>
      {head.status === "pending" ? (
        <ActivityIndicator />
      ) : head.status === "error" ? (
        <>
          <View className="max-w-sm items-center gap-2">
            <Text className="text-center text-xl font-semibold text-foreground">
              Couldn&apos;t load your next card
            </Text>
            <Text className="text-center text-base text-muted-foreground">
              Something went wrong while loading it.
            </Text>
          </View>
          <Button className="mt-2" onPress={head.retry} testID="learn-deck-queue-head-retry">
            <Text>Try Again</Text>
          </Button>
          {goToLearn}
        </>
      ) : (
        <>
          <View className="max-w-sm items-center gap-2">
            <Text className="text-center text-xl font-semibold text-foreground">
              Nothing to study right now
            </Text>
            <Text className="text-center text-base text-muted-foreground">
              Come back when more cards are due.
            </Text>
          </View>
          {goToLearn}
        </>
      )}
    </View>
  );
}
